import React from 'react';
import { Mail, MapPin, ExternalLink, Instagram, Facebook, Youtube, Flame } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { siteConfig, socialLinks, googleForms } from '../mock';
import { useTranslation } from '../i18n/LanguageContext';

const ContactPage = () => {
  const { t } = useTranslation();
  const handleContactClick = () => {
    if (googleForms.contactForm) {
      window.open(googleForms.contactForm, '_blank');
    } else {
      window.location.href = `mailto:${siteConfig.email}`;
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="text-white py-24 md:py-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/images/holy-fire-bg.jpg')" }}></div>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8 fade-in">
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30 uppercase tracking-[0.3em] text-xs">
              {t('contact.badge')}
            </Badge>
            <h1 className="display-hero text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              {t('contact.heroTitle')} <span className="italic font-light">{t('contact.heroAccent')}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto">
              {t('contact.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 md:py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form CTA */}
            <div>
              <div className="mb-8">
                <Badge variant="secondary" className="mb-6 uppercase tracking-[0.3em] text-xs">
                  {t('contact.sendBadge')}
                </Badge>
                <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-3">
                  {t('contact.sendTitle')} <span className="italic font-light text-gradient">{t('contact.sendAccent')}</span>
                </h2>
                <p className="text-muted-foreground">
                  {googleForms.contactForm ? t('contact.sendBodyForm') : t('contact.sendBodyEmail')}
                </p>
              </div>
              <Card className="border-2">
                <CardContent className="p-8 text-center space-y-6">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Flame className="h-10 w-10 text-primary" />
                  </div>
                  <div>
                    <h3 className="display-hero text-2xl md:text-3xl mb-2">{t('contact.getInTouchCard')}</h3>
                    <p className="text-muted-foreground">
                      {googleForms.contactForm ? t('contact.cardBodyForm') : t('contact.cardBodyEmail')}
                    </p>
                  </div>
                  <Button
                    size="lg"
                    className="btn-hover w-full text-lg px-8 py-6"
                    onClick={handleContactClick}
                  >
                    {googleForms.contactForm ? (
                      <>
                        {t('contact.openContactForm')}
                        <ExternalLink className="ml-2 h-5 w-5" />
                      </>
                    ) : (
                      <>
                        {t('contact.sendEmail')}
                        <Mail className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <Badge variant="secondary" className="mb-6 uppercase tracking-[0.3em] text-xs">
                  {t('contact.otherBadge')}
                </Badge>
                <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-3">
                  {t('contact.otherTitle')} <span className="italic font-light text-gradient">{t('contact.otherAccent')}</span>
                </h2>
                <p className="text-muted-foreground">
                  {t('contact.otherBody')}
                </p>
              </div>

              <div className="space-y-4">
                <Card className="card-hover">
                  <CardContent className="flex items-start space-x-4 p-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{t('contact.emailLabel')}</h3>
                      <a
                        href={`mailto:${siteConfig.email}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {siteConfig.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-hover">
                  <CardContent className="flex items-start space-x-4 p-6">
                    <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-destructive" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{t('contact.locationLabel')}</h3>
                      <p className="text-muted-foreground">
                        {t(siteConfig.address)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Social Media */}
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>{t('contact.followUs')}</CardTitle>
                  <CardDescription>
                    {t('contact.followUsBody')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex space-x-3">
                    <a
                      href={socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full btn-hover">
                        <Instagram className="h-5 w-5 mr-2" />
                        Instagram
                      </Button>
                    </a>
                    <a
                      href={socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full btn-hover">
                        <Facebook className="h-5 w-5 mr-2" />
                        Facebook
                      </Button>
                    </a>
                  </div>
                  <a
                    href={socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-3"
                  >
                    <Button variant="outline" className="w-full btn-hover">
                      <Youtube className="h-5 w-5 mr-2" />
                      YouTube
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* Office Hours */}
              <Card className="border-2 bg-primary text-primary-foreground">
                <CardHeader>
                  <CardTitle>{t('contact.availabilityTitle')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{t('contact.availabilityHeadline')}</p>
                    <p className="text-sm mt-2 opacity-90">
                      {t('contact.availabilityBody')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
